// /signin/confirm#t=<token>. The token lives only in the URL fragment: strip
// it at once, peek on load, and consume it ONLY when the user taps
// "Sign me in" (mail scanners that prefetch the link must not burn it).
import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../ui';
import { useAuth } from '../../context/useAuth.jsx';
import { AuthShell, FormMsg } from './AuthShell.jsx';

function readToken() {
  const m = /(?:^#|&)t=([^&]+)/.exec(window.location.hash);
  if (!m) return '';
  try { return decodeURIComponent(m[1]); } catch (e) { return ''; }
}

export function ago(iso) {
  const s = Math.max(0, Math.round((Date.now() - Date.parse(iso)) / 1000));
  if (Number.isNaN(s) || s < 60) return 'just now';
  const mins = Math.round(s / 60);
  if (mins < 60) return `${mins} ${mins === 1 ? 'minute' : 'minutes'} ago`;
  const h = Math.round(mins / 60);
  return `${h} ${h === 1 ? 'hour' : 'hours'} ago`;
}

export function device(ua) {
  const b = /Edg\//.test(ua) ? 'Edge' : /Firefox\//.test(ua) ? 'Firefox' : /Chrome\//.test(ua) ? 'Chrome' : /Safari\//.test(ua) ? 'Safari' : '';
  const o = /Windows/.test(ua) ? 'Windows' : /Android/.test(ua) ? 'Android' : /iPhone|iPad/.test(ua) ? 'iOS' : /Mac OS/.test(ua) ? 'macOS' : /Linux/.test(ua) ? 'Linux' : '';
  if (b && o) return `${b} on ${o}`;
  return b || o || '';
}

export default function ConfirmSignIn() {
  const navigate = useNavigate();
  const { refreshSession } = useAuth();
  // A ref survives the StrictMode effect replay, so the stripped fragment is
  // not lost on the second pass.
  const tokenRef = useRef(null);
  if (tokenRef.current === null) tokenRef.current = readToken();
  const token = tokenRef.current;

  const [who, setWho] = useState({ email: '', detail: 'Checking your link…' });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    window.history.replaceState(window.history.state, '', window.location.pathname + window.location.search);
    if (!token) { navigate('/signin/expired', { replace: true }); return undefined; }
    let alive = true;
    axios.post('/auth/email/peek', { token }, { withCredentials: true }).then((r) => {
      if (!alive) return;
      const d = r.data || {};
      const bits = [];
      if (d.requested_at) bits.push(`Requested ${ago(d.requested_at)}`);
      const dev = device(d.ua || '');
      if (dev) bits.push(dev);
      setWho({ email: d.email_masked || '', detail: bits.join(' · ') });
    }).catch((err) => {
      if (!alive) return;
      if (err?.response?.status === 410) navigate('/signin/expired', { replace: true });
      else setWho({ email: '', detail: '' });
    });
    return () => { alive = false; };
  }, [token, navigate]);

  const signIn = async () => {
    setBusy(true);
    setMsg('');
    try {
      await axios.post('/auth/email/verify-link', { token }, { withCredentials: true });
      const session = await refreshSession({ force: true });
      navigate(session?.activated ? '/app' : '/welcome', { replace: true });
    } catch (err) {
      if (err?.response?.status === 410) navigate('/signin/expired', { replace: true });
      else { setBusy(false); setMsg('Something went wrong. Please try again.'); }
    }
  };

  return (
    <AuthShell
      aside={<><b>Why the extra tap?</b> Some work email systems open links to scan them. This step makes sure your link is used by you, not the scanner.</>}
    >
      <p className="text-sm font-medium text-indigo-600">Nearly there!</p>
      <h1 className="text-2xl font-semibold text-gray-900">One more tap.</h1>
      <p className="text-sm text-gray-600 mt-2">You're signing in to Flowize as</p>
      <div className="flex items-center gap-3 my-5 p-3 rounded-lg border border-gray-200">
        <div className="w-10 h-10 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center font-semibold" aria-hidden="true">
          {(who.email.charAt(0) || '?').toUpperCase()}
        </div>
        <div>
          <b className="block text-gray-900">{who.email || 'your email address'}</b>
          <small className="text-gray-500">{who.detail}</small>
        </div>
      </div>
      <Button variant="primary" size="lg" className="w-full" onClick={signIn} disabled={busy || !token}>
        Sign me in
      </Button>
      <FormMsg error>{msg}</FormMsg>
      <div className="text-center mt-2"><Link className="text-sm text-gray-500 underline" to="/">Not you? Cancel</Link></div>
    </AuthShell>
  );
}
